
export interface AiReviewResult {
  revisedText: string;
  redactions: Array<{
    type: string;
    original: string;
    replacement: string;
  }>;
  notes: string[];
}

export interface AiRiskAssessment {
  issues: Array<{
    start: number;
    end: number;
    type: string;
    severity: 'notice' | 'warning';
    snippet: string;
    reason: string;
  }>;
  categories: {
    toxicity: number;
    harassment: number;
    hate: number;
    pii: number;
    personal_name: number;
  };
  overallSeverity: 'none' | 'notice' | 'warning';
}

export class AiService {
  private static instance: AiService;
  
  public static getInstance(): AiService {
    if (!AiService.instance) {
      AiService.instance = new AiService();
    }
    return AiService.instance;
  }

  /**
   * AI校閲を実行
   */
  async reviewText(text: string, postType: string): Promise<AiReviewResult> {
    try {
      
      // Firebase Functions v2のHTTPSエンドポイントを直接呼び出し
      const { buildFunctionUrl } = await import('../utils/functionsUrl');
      const url = buildFunctionUrl('aiReview');
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ text, type: postType }),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('HTTP Error:', response.status, errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      const data = await response.json();
      
      return {
        revisedText: data.revisedText || text,
        redactions: data.redactions || [],
        notes: data.notes || []
      };
    } catch (error: any) {
      console.error('AI校閲エラー詳細:', {
        message: error.message,
        stack: error.stack
      });
      
      if (error.message?.includes('CORS')) {
        throw new Error('ネットワーク接続に問題があります。ページを再読み込みしてお試しください。');
      } else if (error.message?.includes('HTTP 400')) {
        throw new Error('リクエストの形式が正しくありません。');
      } else if (error.message?.includes('HTTP 500')) {
        throw new Error('AI校閲サービスでエラーが発生しました。しばらく時間をおいて再度お試しください。');
      } else {
        throw new Error(`AI校閲に失敗しました: ${error.message || '不明なエラー'}`);
      }
    }
  }

  /**
   * AIリスク評価を実行
   */
  async riskAssess(text: string, postType: string): Promise<AiRiskAssessment> {
    try {
      
      // Firebase Functions v2のHTTPSエンドポイントを直接呼び出し
      const { buildFunctionUrl } = await import('../utils/functionsUrl');
      const url = buildFunctionUrl('aiRiskAssessment');
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ text, type: postType }),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('HTTP Error:', response.status, errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      const data = await response.json();
      
      return {
        issues: data.issues || [],
        categories: data.categories || {
          toxicity: 0,
          harassment: 0,
          hate: 0,
          pii: 0,
          personal_name: 0
        },
        overallSeverity: data.overallSeverity || 'none'
      };
    } catch (error: any) {
      console.error('AIリスク評価エラー詳細:', {
        message: error.message,
        stack: error.stack
      });
      
      if (error.message?.includes('CORS')) {
        throw new Error('ネットワーク接続に問題があります。');
      } else if (error.message?.includes('HTTP 400')) {
        throw new Error('リクエストの形式が正しくありません。');
      } else if (error.message?.includes('HTTP 500')) {
        throw new Error('AIリスク評価サービスでエラーが発生しました。');
      } else {
        throw new Error(`AIリスク評価に失敗しました: ${error.message || '不明なエラー'}`);
      }
    }
  }

  /**
   * テキストの調整率を計算
   */
  calculateAdjustmentPercent(original: string, revised: string): number {
    // 日本語は空白で区切られないため、単語数差分では常に0%になりやすい。
    // Flutter 版同様、文字ベースの編集距離に基づいて調整率を算出する。
    if (!original || !revised) return 0;

    const o = original.replace(/[\s\u3000]/g, ''); // 半角/全角スペース除去
    const r = revised.replace(/[\s\u3000]/g, '');

    const maxLen = Math.max(o.length, r.length);
    if (maxLen === 0) return 0;

    // レーベンシュタイン距離（編集距離）を使って変更量を推定
    const distance = (() => {
      const m = o.length;
      const n = r.length;
      const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
      for (let i = 0; i <= m; i++) dp[i][0] = i;
      for (let j = 0; j <= n; j++) dp[0][j] = j;
      for (let i = 1; i <= m; i++) {
        for (let j = 1; j <= n; j++) {
          const cost = o[i - 1] === r[j - 1] ? 0 : 1;
          dp[i][j] = Math.min(
            dp[i - 1][j] + 1,       // 削除
            dp[i][j - 1] + 1,       // 追加
            dp[i - 1][j - 1] + cost // 置換
          );
        }
      }
      return dp[m][n];
    })();

    // 調整率（0〜1）に正規化
    const ratio = distance / maxLen;
    return Math.min(Math.max(ratio, 0), 1);
  }
}
